import { DesiredTimeRange, getDesiredTimeRange } from 'utils/waitlistRequestUtils';
import { Instant } from '@js-joda/core';
import {
  PartyForCard,
  getExpectedPartySize,
  getHasGlobalDiningAccess,
  getIsLargeParty,
  getPatronKeyContactAttributes,
  isServiceParty,
} from 'components/service/party/partyRedesignCard/partyType';
import { businessMinLargePartySizeSelector } from 'selectors/businessMinLargePartySizeSelector';
import { getNameForParty, isPartyOnlineWalkIn } from 'utils/partyUtils';
import { guestListShowLastnameFirstNameSelector } from 'selectors/dashboardPreferenceSelectors';
import { useFeatureFlag } from '@tock/shared/utils/featureFlagContext';
import { useSelector } from 'react-redux';

export type PartyTime = string | Instant | DesiredTimeRange | undefined;

export function isTimeRange(time?: PartyTime): time is DesiredTimeRange {
  return typeof time === 'object' && 'startDateTime' in time && 'endDateTime' in time;
}

export function usePartyHeader(party: PartyForCard) {
  const minLargePartySize = useSelector(businessMinLargePartySizeSelector);
  const showLastnameFirstname = useSelector(guestListShowLastnameFirstNameSelector);
  const keyContactAttributes = getPatronKeyContactAttributes(party);
  const isLargeParty = getIsLargeParty(party, minLargePartySize);
  const expectedPartySize = getExpectedPartySize(party);
  const isOnlineWalkIn = isServiceParty(party) && isPartyOnlineWalkIn(party);
  const isAdjustedVisitTimesEnabled = useFeatureFlag('USE_ADJUSTED_VISIT_TIMES');

  const name = useFeatureFlag('FIX_TIMELINE_ZOMBIE_NAME')
    ? getNameForParty({
        ...keyContactAttributes,
        firstNameFirst: !showLastnameFirstname,
        partyIsOnlineWalkIn: isOnlineWalkIn,
      })
    : keyContactAttributes.lastNameFirstName;

  const getPartyCardTime = (party: PartyForCard): PartyTime => {
    if (isServiceParty(party)) {
      const { type, seatedTime, expectedReservationTime, state } = party;

      const shouldShowSeatedTime =
        seatedTime && (state === 'SEATED' || (isAdjustedVisitTimesEnabled && state === 'LEFT'));

      if (type === 'WalkIn') {
        return seatedTime ? Instant.ofEpochSecond(seatedTime) : undefined;
      } else if (shouldShowSeatedTime) {
        return Instant.ofEpochSecond(seatedTime);
      } else {
        return expectedReservationTime ?? undefined;
      }
    } else {
      // party is of type dashboard.CalendarWaitlist
      return getDesiredTimeRange(party.desiredDateTimeRange);
    }
  };
  const time = getPartyCardTime(party);
  const hasGlobalDiningAccess = getHasGlobalDiningAccess(party);

  return {
    isLargeParty,
    expectedPartySize,
    name,
    time,
    hasGlobalDiningAccess,
  };
}
